import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Shield, ChevronLeft, Upload, CheckCircle2, Loader2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import MobileHeader from '../components/MobileHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const DOC_KINDS = [
  { key: 'id_front', label: 'Government ID — Front', required: true },
  { key: 'id_back', label: 'Government ID — Back', required: false },
  { key: 'selfie_with_id', label: 'Selfie holding ID', required: true },
  { key: 'proof_of_address', label: 'Proof of address (utility bill / bank statement)', required: false },
];

const KYCApplication = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [uploads, setUploads] = useState({}); // { kind: {doc_id, name, uploading} }
  const inputRefs = useRef({});
  const [form, setForm] = useState({
    full_name: user?.name || '',
    date_of_birth: '',
    nationality: '',
    address: '',
    city: '',
    postal_code: '',
    country: '',
    id_type: 'passport',
    id_number: '',
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/kyc/status');
        setStatus(res.data);
      } catch (_e) {
        /* silent */
      }
    })();
  }, []);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadFile = async (kind, file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error('File exceeds 6MB limit');
      return;
    }
    setUploads((u) => ({ ...u, [kind]: { uploading: true, name: file.name } }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/kyc/documents/upload?kind=${kind}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploads((u) => ({ ...u, [kind]: { doc_id: res.data.doc_id, name: file.name, uploading: false } }));
      toast.success(`Uploaded ${file.name}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
      setUploads((u) => { const next = { ...u }; delete next[kind]; return next; });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    for (const dk of DOC_KINDS) {
      if (dk.required && !uploads[dk.key]?.doc_id) {
        toast.error(`Please upload: ${dk.label}`);
        return;
      }
    }
    setLoading(true);
    try {
      const kyc_documents = {};
      for (const [kind, info] of Object.entries(uploads)) {
        if (info?.doc_id) kyc_documents[kind] = info.doc_id;
      }
      await api.post('/kyc/submit', {
        kyc_data: form,
        kyc_documents,
      });
      toast.success('KYC submitted — review typically takes 24–48h');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit KYC');
    } finally {
      setLoading(false);
    }
  };

  const already = status?.kyc_status && status.kyc_status !== 'not_submitted';

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)' }} data-testid="kyc-apply-page">
      <MobileHeader />
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 py-8">
        <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-white flex items-center gap-1 mb-4 text-sm" data-testid="kyc-back">
          <ChevronLeft className="h-4 w-4" /> Back
        </button>

        <div className="glow-card p-6 sm:p-8 rounded-2xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <Shield className="h-6 w-6 text-purple-300" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Verify your identity</h1>
              <p className="text-gray-400 text-sm">Complete KYC to unlock deposits, withdrawals, and higher trading limits.</p>
            </div>
          </div>

          {already ? (
            <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/30 text-purple-200 text-sm" data-testid="kyc-already-status">
              Your KYC status is <strong className="uppercase">{status.kyc_status}</strong>. You don't need to resubmit unless requested.
              <div className="mt-4"><Link to="/dashboard" className="text-purple-300 underline">Return to dashboard</Link></div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-6" data-testid="kyc-apply-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300 mb-1 block">Full Legal Name</Label>
                  <Input required value={form.full_name} onChange={onChange('full_name')} className="bg-gray-900/50 border-purple-500/30 text-white" data-testid="kyc-full-name" />
                </div>
                <div>
                  <Label className="text-gray-300 mb-1 block">Date of Birth</Label>
                  <Input required type="date" value={form.date_of_birth} onChange={onChange('date_of_birth')} className="bg-gray-900/50 border-purple-500/30 text-white" data-testid="kyc-dob" />
                </div>
                <div>
                  <Label className="text-gray-300 mb-1 block">Nationality</Label>
                  <Input required value={form.nationality} onChange={onChange('nationality')} className="bg-gray-900/50 border-purple-500/30 text-white" data-testid="kyc-nationality" />
                </div>
                <div>
                  <Label className="text-gray-300 mb-1 block">Country of Residence</Label>
                  <Input required value={form.country} onChange={onChange('country')} className="bg-gray-900/50 border-purple-500/30 text-white" data-testid="kyc-country" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-gray-300 mb-1 block">Address</Label>
                  <Input required value={form.address} onChange={onChange('address')} className="bg-gray-900/50 border-purple-500/30 text-white" data-testid="kyc-address" />
                </div>
                <div>
                  <Label className="text-gray-300 mb-1 block">City</Label>
                  <Input required value={form.city} onChange={onChange('city')} className="bg-gray-900/50 border-purple-500/30 text-white" data-testid="kyc-city" />
                </div>
                <div>
                  <Label className="text-gray-300 mb-1 block">Postal Code</Label>
                  <Input required value={form.postal_code} onChange={onChange('postal_code')} className="bg-gray-900/50 border-purple-500/30 text-white" data-testid="kyc-postal" />
                </div>
                <div>
                  <Label className="text-gray-300 mb-1 block">ID Type</Label>
                  <select value={form.id_type} onChange={onChange('id_type')} className="w-full h-10 rounded-md bg-gray-900/50 border border-purple-500/30 text-white px-3" data-testid="kyc-id-type">
                    <option value="passport">Passport</option>
                    <option value="drivers_license">Driver's License</option>
                    <option value="national_id">National ID</option>
                  </select>
                </div>
                <div>
                  <Label className="text-gray-300 mb-1 block">ID Number</Label>
                  <Input required value={form.id_number} onChange={onChange('id_number')} className="bg-gray-900/50 border-purple-500/30 text-white" data-testid="kyc-id-number" />
                </div>
              </div>

              {/* Documents */}
              <div className="border-t border-gray-800 pt-5">
                <p className="text-white font-semibold text-sm mb-3">Documents</p>
                <div className="space-y-3">
                  {DOC_KINDS.map((dk) => {
                    const info = uploads[dk.key];
                    const uploaded = !!info?.doc_id;
                    return (
                      <div key={dk.key} className={`flex items-center justify-between p-3 rounded-lg border ${uploaded ? 'bg-emerald-500/10 border-emerald-500/40' : 'bg-gray-900/50 border-gray-700'}`} data-testid={`kyc-doc-${dk.key}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-medium">
                            {dk.label} {dk.required && <span className="text-rose-400">*</span>}
                          </p>
                          <p className="text-gray-400 text-xs truncate">
                            {info?.name || 'JPG, PNG, WEBP or PDF · max 6MB'}
                          </p>
                        </div>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,application/pdf"
                          ref={(el) => { inputRefs.current[dk.key] = el; }}
                          onChange={(e) => uploadFile(dk.key, e.target.files?.[0])}
                          className="hidden"
                          data-testid={`kyc-file-${dk.key}`}
                        />
                        <button
                          type="button"
                          onClick={() => inputRefs.current[dk.key]?.click()}
                          disabled={info?.uploading}
                          className="ml-3 px-3 py-2 rounded-md bg-purple-600/80 hover:bg-purple-600 text-white text-xs font-semibold flex items-center gap-1.5 shrink-0"
                          data-testid={`kyc-upload-btn-${dk.key}`}
                        >
                          {info?.uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : uploaded ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Upload className="h-3.5 w-3.5" />}
                          {info?.uploading ? 'Uploading' : uploaded ? 'Replace' : 'Upload'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              <Button type="submit" disabled={loading} className="w-full neo-btn neo-btn-primary py-6 font-semibold" data-testid="kyc-submit-btn">
                {loading ? 'Submitting…' : 'Submit for review'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default KYCApplication;
